// src/layouts/documents/DocumentViewer.js

import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import Footer from "common/Footer";
import Loader from "components/Loader/Loader";
import { TextField } from "@mui/material";
import { useSnackbar } from "context/SnackbarContext";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";

import { Document, Page, pdfjs } from "react-pdf";
import { IconButton, Tabs, Tab, Box, Grid, Card, Tooltip, Select, MenuItem } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ArrowBack,
  ArrowForward,
  ZoomIn,
  ZoomOut,
  Delete,
  Download,
  Print,
  CheckCircle,
} from "@mui/icons-material";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Import your API instance
import api from "../../services/api";

// Import React Query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import InvoiceDetails from "common/DocumentViewer/InvoiceDetails/InvoiceDetails";
import InvoiceLineItems from "common/DocumentViewer/InvoiceLineItems/InvoiceLineItems";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function DocumentViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { selectedCompany } = useCompany(); // Pridėkite šią eilutę čia
  const { t } = useLanguage();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const pdfContainerRef = useRef(null);
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0 });

  // Local state for document data
  const [documentData, setDocumentData] = useState(null);
  const [notes, setNotes] = useState("");
  const [lineItems, setLineItems] = useState([]);

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [zoom, setZoom] = useState(1.0);
  const [containerWidth, setContainerWidth] = useState(0);

  // Zoom and page control functions
  const handleZoomIn = () => setZoom((prevZoom) => Math.min(prevZoom + 0.1, 3.0));
  const handleZoomOut = () => setZoom((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
  const goToPrevPage = () => setPageNumber((prevPageNumber) => Math.max(prevPageNumber - 1, 1));
  const goToNextPage = () =>
    setPageNumber((prevPageNumber) => Math.min(prevPageNumber + 1, numPages));

  // Function to handle container size
  const handleResize = () => {
    const container = document.getElementById("pdf-container");
    if (container) {
      setContainerWidth(container.clientWidth);
    }
  };

  // Listen to window resize events
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleWheel = (e) => {
    // Don't call preventDefault to avoid passive listener warnings.
    // We still adjust zoom slightly on wheel; scrolling will continue to work.
    const zoomFactor = e.deltaY < 0 ? 0.05 : -0.05;
    setZoom((prevZoom) => Math.max(0.5, Math.min(prevZoom + zoomFactor, 3.0)));
  };

  const handleMouseDown = (e) => {
    const container = pdfContainerRef.current;
    if (!container || e.target.closest("input, button, a, textarea") || e.button !== 0) {
      return;
    }
    e.preventDefault();
    isPanningRef.current = true;
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollTop: container.scrollTop,
      scrollLeft: container.scrollLeft,
    };
    e.currentTarget.style.cursor = "grabbing";
    e.currentTarget.style.userSelect = "none";
  };

  const handleMouseUpAndLeave = (e) => {
    isPanningRef.current = false;
    e.currentTarget.style.cursor = "grab";
    e.currentTarget.style.userSelect = "auto";
  };

  const handleMouseMove = (e) => {
    const container = pdfContainerRef.current;
    if (!isPanningRef.current || !container) return;
    e.preventDefault();
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    container.scrollTop = panStartRef.current.scrollTop - dy;
    container.scrollLeft = panStartRef.current.scrollLeft - dx;
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const dynamicScale = useMemo(() => {
    const baseScale = containerWidth ? containerWidth / 800 : 1;
    return baseScale * zoom;
  }, [containerWidth, zoom]);

  // UseQuery to fetch document data
  const { data, isLoading, error } = useQuery({
    queryKey: ["document", id],
    queryFn: async () => {
      try {
        console.log(`Fetching document data for ID: ${id}`);
        const response = await api.get(`/api/documents/${id}/`);
        console.log("Document data fetched:", response.data);
        return response.data;
      } catch (err) {
        console.error("Error fetching document data:", err);
        throw err;
      }
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
  });

  // Update local state when data changes
  useEffect(() => {
    if (data) {
      console.log("Updating state with document data:", data);
      console.log("Formatted text:", data.formatted_text);
      setDocumentData(data);
      setNotes(data.notes || "");
      setLineItems(data.line_items || []);
    }
  }, [data]);

  // Mutation: Save Notes
  const saveNotesMutation = useMutation({
    mutationFn: async (newNotes) => {
      console.log("Saving notes:", newNotes);
      const response = await api.post(`/api/documents/${id}/save_notes/`, { notes: newNotes });
      return response.data;
    },
    onSuccess: () => {
      showSnackbar(t("notesSavedSuccessfully"), "success");
      queryClient.invalidateQueries(["document", id]); // Atnaujina dabartinį dokumentą
      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error saving notes:", error);
      showSnackbar(t("errorSavingNotes"), "error");
    },
  });

  // Mutation: Save Details
  const saveDetailsMutation = useMutation({
    mutationFn: async (updatedData) => {
      console.log("Saving document details:", updatedData);
      const response = await api.post(`/api/documents/${id}/update-data/`, updatedData);
      return response.data;
    },
    onSuccess: () => {
      showSnackbar(t("informationSavedSuccessfully"), "success");
      queryClient.invalidateQueries(["document", id]); // Atnaujina dabartinį dokumentą
      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error saving document details:", error);
      showSnackbar(t("errorSavingInformation"), "error");
    },
  });

  // Mutation: Save Line Items
  const saveLineItemsMutation = useMutation({
    mutationFn: async (updatedLineItems) => {
      console.log("Saving line items:", updatedLineItems);
      const response = await api.post(`/api/documents/${id}/update-line-items/`, {
        line_items: updatedLineItems,
      });
      return response.data;
    },
    onSuccess: (data) => {
      showSnackbar(t("lineItemsSavedSuccessfully"), "success");

      // Jei backend grąžino atnaujintą dokumentą, atnaujinkime cache
      if (data.document) {
        queryClient.setQueryData(["document", id], data.document);
        // Atnaujinkime ir local state
        setDocumentData(data.document);
        setLineItems(data.document.line_items || []);
      } else {
        // Jei ne, tiesiog invaliduokime
        queryClient.invalidateQueries(["document", id]);
      }

      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error saving line items:", error);
      showSnackbar(t("errorSavingLineItems"), "error");
    },
  });

  // Mutation: Recognize Document
  const recognizeMutation = useMutation({
    mutationFn: async () => {
      console.log("Recognizing document with ID:", id);
      const response = await api.post(`/api/recognize/`, { document_ids: [id] });
      return response.data;
    },
    onSuccess: () => {
      showSnackbar(t("documentRecognizedSuccessfully"), "success");
      queryClient.invalidateQueries(["document", id]); // Atnaujina dabartinį dokumentą
      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error recognizing document:", error);
      showSnackbar(t("errorRecognizingDocument"), "error");
    },
  });

  // Mutation: Delete Document
  const deleteMutation = useMutation({
    mutationFn: async () => {
      console.log("Deleting document with ID:", id);
      const response = await api.delete(`/api/documents/${id}/`);
      return response.data;
    },
    onSuccess: () => {
      showSnackbar(t("documentDeletedSuccessfully"), "success");
      navigate("/documents");
      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error deleting document:", error);
      showSnackbar(t("errorDeletingDocument"), "error");
    },
  });

  // Download document (no mutation needed)
  const handleDownload = () => {
    console.log("Downloading document:", documentData.file_url);
    const link = document.createElement("a");
    link.href = documentData.file_url;
    link.download = documentData.title || "document.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print document (no mutation needed)
  const handlePrint = () => {
    console.log("Printing document:", documentData.file_url);
    const printWindow = window.open(documentData.file_url, "_blank");
    printWindow.focus();
    printWindow.print();
  };

  // Dependencies: query status
  if (isLoading) {
    console.log("Loading document data...");
    return (
      <DashboardLayout>
        <MDBox pt={6} pb={3}>
          <Loader fullscreen />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    console.error("Error in DocumentViewer:", error);
    return (
      <DashboardLayout>
        <MDBox pt={6} pb={3}>
          <MDTypography variant="h4" color="error">
            Error fetching document data
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!documentData) {
    console.warn("No document data available. documentData is:", documentData);
    return (
      <DashboardLayout>
        <MDBox pt={6} pb={3}>
          <MDTypography variant="h4" color="error">
            Document data is not available
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!documentData.data) {
    console.warn("Document data object is missing 'data' property. documentData:", documentData);
    return (
      <DashboardLayout>
        <MDBox pt={6} pb={3}>
          <MDTypography variant="h4" color="error">
            Document data structure is invalid
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  console.log("DocumentViewer - Document data is valid:", documentData);

  const zoomPresets = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 3.0];
  const zoomOptions = [...new Set([...zoomPresets, zoom])].sort((a, b) => a - b);

  return (
    <DashboardLayout>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={2}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems={isMdDown ? "flex-start" : "center"}
                  flexDirection={isMdDown ? "column" : "row"}
                  gap={isMdDown ? 1 : 0}
                  mb={2}
                  sx={{ minWidth: 0 }}
                >
                  <MDBox display="flex" alignItems="center" gap={2} sx={{ minWidth: 0 }}>
                    <MDButton
                      variant="outlined"
                      color="info"
                      onClick={() => {
                        // Prefer browser back to preserve list state; fallback to route
                        if (window.history.length > 1) {
                          navigate(-1);
                        } else {
                          navigate("/documents");
                        }
                      }}
                      startIcon={<ArrowBack />}
                    >
                      {t("backToDocuments")}
                    </MDButton>
                    {!isMdDown && (
                      <MDTypography
                        variant="h5"
                        fontWeight="medium"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          maxWidth: "50vw",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={documentData.title || ""}
                      >
                        {documentData.title}
                      </MDTypography>
                    )}
                  </MDBox>
                  <Box sx={{ flex: 1, mr: "auto", ml: isMdDown ? 0 : 2, mb: isMdDown ? 1 : 0 }} />
                  {isMdDown && (
                    <MDTypography
                      variant="subtitle1"
                      fontWeight="medium"
                      sx={{
                        width: "100%",
                        mt: 0.5,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                      title={documentData.title || ""}
                    >
                      {documentData.title}
                    </MDTypography>
                  )}
                  <MDBox display="flex" gap={1} flexWrap={isMdDown ? "wrap" : "nowrap"}>
                    <Tooltip title={t("previousDocument")}>
                      <span style={{ display: "inline-flex" }}>
                        <IconButton color="info" disabled>
                          <ArrowBack />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("nextDocument")}>
                      <span style={{ display: "inline-flex" }}>
                        <IconButton color="info" disabled>
                          <ArrowForward />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("recognizeDocument")}>
                      <span style={{ display: "inline-flex" }}>
                        <MDButton
                          variant="contained"
                          color="info"
                          onClick={() => recognizeMutation.mutate()}
                          disabled={documentData.is_recognized}
                          startIcon={<CheckCircle />}
                        >
                          {t("recognize")}
                        </MDButton>
                      </span>
                    </Tooltip>
                  </MDBox>
                </MDBox>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: "100%" }}>
                      <MDBox p={2}>
                        {isMdDown ? (
                          <Select
                            value={tabValue}
                            onChange={(e) => setTabValue(e.target.value)}
                            size="small"
                            fullWidth
                            sx={{ mb: 2 }}
                            MenuProps={{
                              disableScrollLock: true,
                              PaperProps: { sx: { maxHeight: 360 } },
                            }}
                          >
                            <MenuItem value={0}>{t("documentDetails")}</MenuItem>
                            <MenuItem value={1}>{t("recognizedText")}</MenuItem>
                            <MenuItem value={2}>{t("notes")}</MenuItem>
                            <MenuItem value={3}>{t("lineItems")}</MenuItem>
                            <MenuItem value={4}>{t("formattedText")}</MenuItem>
                          </Select>
                        ) : (
                          <Tabs
                            value={tabValue}
                            onChange={(event, newValue) => setTabValue(newValue)}
                            variant="scrollable"
                            sx={{
                              borderBottom: 1,
                              borderColor: "divider",
                              mb: 2,
                            }}
                          >
                            <Tab label={t("documentDetails")} />
                            <Tab label={t("recognizedText")} />
                            <Tab label={t("notes")} />
                            <Tab label={t("lineItems")} />
                            <Tab label={t("formattedText")} />
                          </Tabs>
                        )}

                        <MDBox
                          sx={{
                            height: isMdDown ? "auto" : "calc(100vh - 400px)",
                            maxHeight: isMdDown ? "none" : "calc(100vh - 400px)",
                            overflowY: isMdDown ? "visible" : "auto",
                          }}
                        >
                          {tabValue === 0 && (
                            <MDBox>
                              <InvoiceDetails
                                documentData={documentData}
                                setDocumentData={setDocumentData}
                                handleDetailsSave={() => {
                                  saveDetailsMutation.mutate(documentData.data);
                                }}
                              />
                            </MDBox>
                          )}

                          {tabValue === 1 && (
                            <MDBox>
                              <MDTypography variant="h6" mb={2}>
                                {t("recognizedText")}
                              </MDTypography>
                              <Box
                                sx={{
                                  backgroundColor: "background.paper",
                                  p: 2,
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: "divider",
                                }}
                              >
                                <pre
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    wordWrap: "break-word",
                                    fontFamily: "inherit",
                                    margin: 0,
                                    fontSize: "0.875rem",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {documentData.recognized_text || t("noRecognizedTextAvailable")}
                                </pre>
                              </Box>
                            </MDBox>
                          )}

                          {tabValue === 2 && (
                            <MDBox>
                              <MDTypography variant="h6" mb={2}>
                                {t("notes")}
                              </MDTypography>
                              <TextField
                                label={t("notes")}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                fullWidth
                                multiline
                                rows={6}
                                variant="outlined"
                                sx={{ mb: 2 }}
                              />
                              <MDButton
                                variant="contained"
                                color="info"
                                onClick={() => saveNotesMutation.mutate(notes)}
                                startIcon={<CheckCircle />}
                              >
                                {t("saveNotes")}
                              </MDButton>
                            </MDBox>
                          )}

                          {tabValue === 3 && (
                            <MDBox>
                              <MDTypography variant="h6" mb={2}>
                                {t("lineItems")}
                              </MDTypography>
                              <InvoiceLineItems
                                lineItems={lineItems}
                                onSave={(updatedLineItems) => {
                                  setLineItems(updatedLineItems);
                                  saveLineItemsMutation.mutate(updatedLineItems);
                                }}
                              />
                            </MDBox>
                          )}

                          {tabValue === 4 && (
                            <MDBox>
                              {documentData?.data && (
                                <Card sx={{ mb: 2 }}>
                                  <MDBox p={2}>
                                    <MDTypography variant="h6" mb={1}>
                                      {t("tokenUsage")}
                                    </MDTypography>
                                    <MDTypography variant="body2">
                                      {t("promptTokens")}: {documentData.data.prompt_tokens || 0}
                                    </MDTypography>
                                    <MDTypography variant="body2">
                                      {t("completionTokens")}:{" "}
                                      {documentData.data.completion_tokens || 0}
                                    </MDTypography>
                                    <MDTypography variant="body2" fontWeight="bold">
                                      {t("totalTokens")}: {documentData.data.total_tokens || 0}
                                    </MDTypography>
                                  </MDBox>
                                </Card>
                              )}
                              <MDTypography variant="h6" mb={2}>
                                {t("formattedText")}
                              </MDTypography>
                              <Box
                                sx={{
                                  backgroundColor: "background.paper",
                                  p: 2,
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: "divider",
                                }}
                              >
                                <pre
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    wordWrap: "break-word",
                                    fontFamily: "inherit",
                                    margin: 0,
                                    fontSize: "0.875rem",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {documentData?.formatted_text || t("noFormattedTextAvailable")}
                                </pre>
                              </Box>
                            </MDBox>
                          )}
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: "100%" }}>
                      <MDBox p={2}>
                        <MDTypography variant="h6" mb={2}>
                          {t("documentPreview")}
                        </MDTypography>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                          sx={{
                            backgroundColor: "background.paper",
                            p: 1,
                            borderRadius: 1,
                            border: 1,
                            borderColor: "divider",
                            flexWrap: isMdDown ? "wrap" : "nowrap",
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <Tooltip title={t("zoomOut")}>
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  onClick={handleZoomOut}
                                  size="small"
                                  disabled={zoom <= 0.5}
                                >
                                  <ZoomOut />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Select
                              value={zoom}
                              onChange={(e) => setZoom(e.target.value)}
                              size="small"
                              sx={{
                                minWidth: 90,
                                height: 32,
                                "& .MuiSelect-select": {
                                  paddingTop: "6px",
                                  paddingBottom: "6px",
                                  paddingLeft: "10px",
                                },
                              }}
                              MenuProps={{ disableScrollLock: true }}
                            >
                              {zoomOptions.map((z) => (
                                <MenuItem key={z} value={z}>
                                  {`${Math.round(z * 100)}%`}
                                </MenuItem>
                              ))}
                            </Select>
                            <Tooltip title={t("zoomIn")}>
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  onClick={handleZoomIn}
                                  size="small"
                                  disabled={zoom >= 3.0}
                                >
                                  <ZoomIn />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={t("previousPage")}>
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  onClick={goToPrevPage}
                                  disabled={pageNumber <= 1}
                                  size="small"
                                >
                                  <ArrowBack />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <MDTypography variant="body2" component="span">
                              {t("page")} {pageNumber} {t("of")} {numPages}
                            </MDTypography>
                            <Tooltip title={t("nextPage")}>
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  onClick={goToNextPage}
                                  disabled={pageNumber >= numPages}
                                  size="small"
                                >
                                  <ArrowForward />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </MDBox>
                          <MDBox display="flex" gap={1}>
                            <Tooltip title={t("deleteDocument")}>
                              <IconButton
                                onClick={() => {
                                  if (window.confirm(t("confirmDeleteDocument"))) {
                                    deleteMutation.mutate();
                                  }
                                }}
                                color="error"
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("downloadDocument")}>
                              <IconButton onClick={handleDownload} size="small">
                                <Download />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("printDocument")}>
                              <IconButton onClick={handlePrint} size="small">
                                <Print />
                              </IconButton>
                            </Tooltip>
                          </MDBox>
                        </MDBox>
                        <MDBox
                          sx={{
                            height: isMdDown ? "60vh" : "calc(100vh - 400px)",
                            overflow: "auto",
                            backgroundColor: "background.paper",
                            borderRadius: 1,
                            border: 1,
                            borderColor: "divider",
                            p: 2,
                            cursor: "grab",
                            // Hide scrollbar for Chrome, Safari and Opera
                            "&::-webkit-scrollbar": {
                              display: "none",
                            },
                            // Hide scrollbar for IE, Edge and Firefox
                            msOverflowStyle: "none", // IE and Edge
                            scrollbarWidth: "none", // Firefox
                          }}
                          id="pdf-container"
                          ref={pdfContainerRef}
                          onWheel={handleWheel}
                          onMouseDown={handleMouseDown}
                          onMouseUp={handleMouseUpAndLeave}
                          onMouseLeave={handleMouseUpAndLeave}
                          onMouseMove={handleMouseMove}
                        >
                          {documentData.file_url ? (
                            <Document
                              file={documentData.file_url}
                              onLoadSuccess={onDocumentLoadSuccess}
                              onLoadError={(error) =>
                                console.error("Error loading document:", error)
                              }
                            >
                              <Page pageNumber={pageNumber} scale={dynamicScale} />
                            </Document>
                          ) : (
                            <MDTypography variant="body1" color="error">
                              {t("documentFileUrlMissing")}
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DocumentViewer;
